import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  MenuItem,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  PersonAdd as RegisterIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { AuthPageLayout } from '../components/AuthPageLayout';
import { PhoneVerification } from '../components/PhoneVerification';
import {
  sendSmsCode,
  verifySmsCode,
  formatPhoneNumber,
} from '../services/smsService';
import { createFirebaseUserAndSendVerification } from '../services/firebaseAuth';

/**
 * 服務條款內容
 * 管理員若需修改條款文字，請直接編輯此常數，
 * 並同步更新專案根目錄的「使用者服務條款與同意書.md」。
 */
const TERMS_OF_SERVICE = `
# 紫微斗數算命系統 - 使用者服務條款與同意書

**生效日期：2026年1月5日**

---

歡迎使用本紫微斗數算命系統（以下簡稱「本系統」）。在您註冊並開始使用本系統之前，請您仔細閱讀以下服務條款與同意書（以下簡稱「本條款」）。一旦您點擊「同意」按鈕並完成註冊，即表示您已完全理解、同意並接受本條款的所有內容。

---

## 第一條 服務性質與免責聲明

### 1.1 服務對象限制
**本系統僅服務年滿 15 歲至 80 歲的客戶。** 註冊時系統將驗證您的出生年份與年齡，若不符合以下要求，將無法完成註冊或使用相關功能：

- **註冊年齡限制**：申請註冊時，使用者實際年齡必須在 15 歲（含）至 80 歲（含）之間

使用者註冊即表示確認符合上述年齡與出生年份要求。若提供不實資訊，本公司有權立即終止服務而無需退費或賠償。

### 1.2 娛樂性質
本系統是一個基於紫微斗數理論的算命系統，所提供的所有分析結果、運勢預測、配對分析、性格解析等內容，**僅供娛樂參考使用**，不構成任何專業建議或保證。

### 1.3 結果準確性免責
本系統所呈現的所有分析結果、預測內容、建議事項等資訊，其準確性、完整性、適用性，**本公司概不負責**。使用者不應將本系統的結果作為任何重大決策（包括但不限於投資、婚姻、創業、醫療等）的唯一依據。

### 1.4 費用性質說明
使用者在本系統所支付的任何費用，均為**資訊處理、系統運算、資料儲存等技術服務的費用**，而非購買算命結果的準確性保證。支付費用不代表本公司對分析結果的準確性負有任何責任。

### 1.5 個人判斷責任
使用者應基於自身理性判斷，審慎參考本系統提供的資訊。因使用本系統內容所產生的任何決策後果，完全由使用者自行承擔，與本公司無關。

---

## 第二條 第三方商品與服務

### 2.1 商品來源
本系統可能提供第三方廠商的商品或服務供使用者購買（以下簡稱「內購商品」），包括但不限於實體商品、課程、顧問服務等。

### 2.2 商品責任歸屬
所有內購商品的品質、交付、售後服務等相關事宜，**完全由商品提供廠商負責**。本公司僅提供交易平台，不對內購商品的任何問題承擔責任。

### 2.3 爭議處理
使用者對於內購商品有任何疑問、投訴或退換貨需求，應直接聯繫商品提供廠商處理。本公司不介入使用者與第三方廠商之間的任何糾紛。

---

## 第三條 先服務後付費機制

### 3.1 服務提供流程
本系統提供的資訊服務（**不含內購商品**）採用「**先提供服務，顧客滿意後再付費**」的機制。使用者可先查看分析結果，確認滿意後再選擇是否付費。

### 3.2 付費即表示滿意
**一旦使用者完成付費，即視為使用者完全滿意本系統所提供的服務內容**，包括但不限於分析結果的呈現方式、內容完整性、解析深度等。

### 3.3 付費後無追責權利
使用者完成付費後，**無權以任何理由（包括但不限於結果不準確、不符合期望、內容不滿意等）要求退費、賠償或追究本公司責任**。如使用者對服務內容有任何疑慮，應在付費前審慎評估。

### 3.4 例外情況
若因本系統技術故障導致無法正常顯示內容，或因本公司過失導致服務未能提供，使用者可聯繫客服申請退費。

---

## 第四條 服務提供權利

### 4.1 拒絕服務權利
本公司保留**在任何時刻、以任何理由、無需事先通知或說明，完全拒絕向任何使用者提供服務的權利**，包括但不限於：
- 拒絕註冊申請
- 暫停或終止使用者帳號
- 拒絕處理特定問題查詢
- 限制使用者使用特定功能

### 4.2 拒絕服務情形
本公司可能在以下情況（但不限於）拒絕提供服務：
- 使用者違反本條款或相關法律法規
- 使用者提供虛假資訊或濫用系統
- 使用者行為損害本公司或其他使用者權益
- 使用者有惡意攻擊、騷擾等不當行為
- 本公司基於營運、技術、法律等考量

### 4.3 無賠償義務
本公司行使拒絕服務權利時，無需對使用者進行任何賠償或補償，使用者已支付的費用亦不予退還（技術故障等本公司過失情形除外）。

---

## 第五條 資料與版權條款

### 5.1 使用者資料所有權
使用者在本系統中輸入的所有個人資訊、生辰八字、查詢記錄、使用行為等資料（以下簡稱「使用者資料」），其**所有權、使用權及相關權利歸本公司所有**。

### 5.2 系統內容版權
本系統提供的所有分析結果、預測內容、報告文字、圖表呈現、演算法邏輯等資訊（以下簡稱「系統內容」），其**著作權、版權及相關智慧財產權歸本公司所有**。

### 5.3 資料使用授權
使用者同意本公司有權以下列方式使用使用者資料及系統內容：
- 用於改進系統功能、提升服務品質
- 進行數據分析、統計研究（匿名化處理）
- 開發新功能、訓練演算法模型
- 用於行銷推廣、案例展示（匿名化處理）
- 法律法規要求的其他用途

### 5.4 使用限制
使用者不得以任何方式：
- 複製、傳播、出版本系統內容用於商業用途
- 逆向工程、反編譯或破解本系統
- 將本系統內容提供給第三方使用
- 聲稱對系統內容擁有任何權利

### 5.5 隱私保護
儘管資料所有權歸本公司，本公司仍承諾：
- 不會將使用者個人隱私資料（姓名、聯絡方式等）洩露給第三方
- 在對外使用資料時進行匿名化處理
- 採取合理技術措施保護資料安全
- 遵守相關隱私保護法律法規

---

## 第六條 付費與退費政策

### 6.1 付費方式
本系統支援多種付費方式，具體以系統頁面顯示為準。所有交易均透過安全加密通道進行。

### 6.2 價格調整
本公司保留隨時調整服務價格的權利，調整後的價格將即時生效。使用者已支付的費用不受價格調整影響。

### 6.3 退費政策
- **一般情況**：依第三條規定，使用者付費後不得要求退費
- **技術故障**：因本系統技術問題導致服務未提供，可申請退費
- **內購商品**：依商品提供廠商的退換貨政策辦理
- **退費處理**：符合退費條件者，將於申請核准後 7-14 個工作日內退款

### 6.4 發票開立
本公司將依法開立發票或收據，使用者應確保提供正確的發票資訊。

---

## 第六條之一 註冊贈送點數規則

### 6-1.1 點數贈送
新使用者完成註冊後，本系統將贈送 **499 點** 的體驗點數（以下簡稱「贈送點數」）。

### 6-1.2 點數使用範圍
贈送點數**僅限**用於本系統提供的問命服務（包括但不限於每日運勢、命盤分析、配對分析等問答功能）。使用時，1 點等同於 NTD 1 元。

### 6-1.3 點數使用限制
贈送點數具有以下使用限制：
- **不可兌換現金**：贈送點數無法兌換為現金或任何等值貨幣
- **不可轉讓**：贈送點數僅限本人帳號使用，不得轉讓、贈與、買賣或移轉給他人
- **不可移作他用**：贈送點數僅能用於本系統問命服務，不可用於購買內購商品或其他用途
- **不可累積**：每個帳號僅能獲得一次註冊贈送點數

### 6-1.4 點數有效期限
贈送點數自註冊成功之日起 **30 天內** 有效。逾期未使用的點數將自動歸零，恕不補發。

### 6-1.5 點數扣款優先順序
當使用者使用問命服務時，系統將優先扣除贈送點數，待贈送點數用盡後，再扣除帳戶餘額（已儲值金額）。

### 6-1.6 點數查詢
使用者可於個人中心查看目前的贈送點數餘額及到期日期。

### 6-1.7 修改權利
本公司保留隨時修改贈送點數規則的權利，包括但不限於點數數量、有效期限、使用範圍等。修改後的規則將即時生效，不影響已贈送的點數。

---

## 第六條之二 欠款與透支規則

### 6-2.1 透支服務
本系統提供「先享受、後付款」的服務模式。當使用者的帳戶餘額與贈送點數均已用盡時，仍可繼續使用問命服務，系統將記錄相應的欠款金額。

### 6-2.2 透支上限
使用者的欠款金額上限為 **NTD 500**。在欠款金額未超過此上限前，使用者仍可正常使用問命服務。

### 6-2.3 服務暫停
當使用者的欠款金額超過 NTD 500 時，系統將暫停提供問命服務，直至使用者完成加值並將欠款歸零後，方可恢復服務。

### 6-2.4 欠款處理
使用者應儘速加值以清償欠款。加值後，系統將優先以儲值金額償還欠款，餘額方可用於後續服務。欠款清償須由管理員確認後，方可恢復正常服務。

### 6-2.5 重複註冊限制
為防止惡意欠款，同一電話號碼最多只能被註冊兩次。若有欠款未清償的電話號碼或出生資料，將無法進行新的註冊。

### 6-2.6 付款承諾
使用透支服務代表使用者同意將於日後清償欠款。使用本系統服務並付款，代表使用者完全滿意本系統所提供的服務品質。

---

## 第七條 帳號管理與安全

### 7.1 帳號責任
使用者應妥善保管帳號密碼，不得將帳號借予他人使用。所有使用該帳號進行的行為，均視為使用者本人的行為。

### 7.2 帳號安全
使用者發現帳號被盜用或有安全疑慮時，應立即通知本公司。因使用者未妥善保管帳號導致的損失，由使用者自行承擔。

### 7.3 帳號終止
使用者可隨時申請刪除帳號。本公司亦可因使用者違反本條款而暫停或終止其帳號，無需事前通知。

---

## 第八條 系統使用規範

### 8.1 禁止行為
使用者不得從事以下行為：
- 利用本系統進行詐騙、非法活動
- 上傳惡意程式、病毒或有害資料
- 干擾系統正常運作或攻擊系統安全
- 濫用系統資源或進行大量異常查詢
- 冒充他人或提供虛假資訊
- 侵害他人或本公司的合法權益

### 8.2 違規處理
使用者違反本條款時，本公司有權：
- 立即暫停或終止其帳號
- 刪除違規內容或資料
- 拒絕提供服務且不退還費用
- 保留法律追訴權

---

## 第九條 免責與限制條款

### 9.1 不可抗力
因天災、戰爭、政府行為、網路故障、駭客攻擊等不可抗力因素導致服務中斷或損失，本公司不承擔責任。

### 9.2 第三方服務
本系統可能連結至第三方網站或服務，本公司對第三方內容不承擔任何責任。

### 9.3 責任限制
在法律允許的最大範圍內，本公司對任何直接、間接、附帶、特殊、懲罰性損害不承擔責任。

---

## 第十條 條款修改與通知

### 10.1 修改權利
本公司保留隨時修改本條款的權利。修改後的條款將公布於系統頁面，並自公布之日起生效。

### 10.2 修改通知
重大條款修改時，本公司將透過系統通知、電子郵件等方式告知使用者。使用者繼續使用系統，視為同意修改後的條款。

### 10.3 通知方式
本公司對使用者的通知，可透過系統訊息、電子郵件、簡訊等方式進行。使用者應確保聯絡資訊正確有效。

---

## 第十一條 爭議解決

### 11.1 準據法
本條款的解釋、效力及爭議解決，適用中華民國法律。

### 11.2 管轄法院
因本條款產生的爭議，雙方同意以台灣台北地方法院為第一審管轄法院。

### 11.3 協商解決
發生爭議時，雙方應優先透過友好協商解決。協商不成時，再循法律途徑處理。

---

## 第十二條 其他條款

### 12.1 完整協議
本條款構成使用者與本公司之間關於使用本系統的完整協議，取代先前的任何口頭或書面約定。

### 12.2 可分割性
若本條款任何條款被認定無效或不可執行，不影響其他條款的效力。

### 12.3 語言版本
本條款以繁體中文版本為準。如有其他語言版本，繁體中文版本具優先效力。

### 12.4 聯絡方式
如對本條款有任何疑問或需要協助，請透過系統內的客服功能聯繫我們。

---

## 使用者同意聲明

使用者在按下「同意，並繼續註冊」按鈕後，代表已知悉並同意以下所有項目：

- 我已仔細閱讀並完全理解本《使用者服務條款與同意書》的所有內容
- 我確認本人年齡介於 15 歲至 80 歲之間，符合系統服務對象要求
- 我同意本系統僅供娛樂參考，分析結果不構成任何保證
- 我理解支付的費用為資訊處理費用，本公司對結果準確性不負責
- 我同意內購商品由商品提供廠商負責，與本公司無關
- 我理解付費後即表示完全滿意，無事後追責權利
- 我同意本公司有權隨時拒絕提供服務
- 我同意本公司對使用者資料及系統內容擁有版權
- 我理解贈送點數僅能用於問命服務，不可兌換現金或轉讓
- 我理解贈送點數於註冊後 30 天內有效，逾期歸零
- 我理解本系統允許透支至 NTD 500，超過後將暫停服務直到加值
- 我同意遵守本條款的所有規定

---

© 2026 紫微斗數算命系統 版權所有 All Rights Reserved
`;

/**
 * 時辰選項 (限定值)
 */
const HOUR_OPTIONS = [
  { value: '23_early', label: '子時 (00:01-01:00)' },
  { value: '01', label: '丑時 (01:01-03:00)' },
  { value: '03', label: '寅時 (03:01-05:00)' },
  { value: '05', label: '卯時 (05:01-07:00)' },
  { value: '07', label: '辰時 (07:01-09:00)' },
  { value: '09', label: '巳時 (09:01-11:00)' },
  { value: '11', label: '午時 (11:01-13:00)' },
  { value: '13', label: '未時 (13:01-15:00)' },
  { value: '15', label: '申時 (15:01-17:00)' },
  { value: '17', label: '酉時 (17:01-19:00)' },
  { value: '19', label: '戌時 (19:01-21:00)' },
  { value: '21', label: '亥時 (21:01-23:00)' },
  { value: '23_late', label: '子時 (23:01-24:00)' },
];

/**
 * 性別選項
 */
const GENDER_OPTIONS = [
  { value: 'Male', label: '男' },
  { value: 'Female', label: '女' },
];

/**
 * 國碼選項（華人地區）
 */
const COUNTRY_CODE_OPTIONS = [
  { value: '+886', label: '台灣 (+886)', placeholder: '0912345678', pattern: /^0?9\d{8}$/, maxLength: 10 },
  { value: '+86', label: '中國 (+86)', placeholder: '13812345678', pattern: /^1\d{10}$/, maxLength: 11 },
  { value: '+852', label: '香港 (+852)', placeholder: '91234567', pattern: /^[5-9]\d{7}$/, maxLength: 8 },
  { value: '+853', label: '澳門 (+853)', placeholder: '62345678', pattern: /^6\d{7}$/, maxLength: 8 },
  { value: '+65', label: '新加坡 (+65)', placeholder: '81234567', pattern: /^[89]\d{7}$/, maxLength: 8 },
  { value: '+60', label: '馬來西亞 (+60)', placeholder: '123456789', pattern: /^1\d{8,9}$/, maxLength: 10 },
];

/**
 * 註冊表單驗證規則
 */
const validationSchema = Yup.object({
  email: Yup.string()
    .email('Email 格式錯誤')
    .required('請輸入 Email'),
  password: Yup.string()
    .min(6, '密碼長度至少 6 個字元')
    .required('請輸入密碼'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], '兩次密碼輸入不一致')
    .required('請確認密碼'),
  full_name: Yup.string()
    .min(2, '姓名或代稱至少 2 個字元')
    .max(50, '姓名或代稱最多 50 個字元')
    .required('請輸入姓名或代稱'),
  country_code: Yup.string()
    .required('請選擇國碼'),
  phone_number: Yup.string()
    .required('請輸入手機號碼')
    .test('valid-phone', '請輸入有效的手機號碼', function(value) {
      const { country_code } = this.parent;
      if (!value || !country_code) return false;
      const option = COUNTRY_CODE_OPTIONS.find(o => o.value === country_code);
      if (!option) return false;
      return option.pattern.test(value);
    }),
  secret_code: Yup.string()
    .matches(/^\d{4}$/, '個人專屬數字必須為 4 位數字')
    .required('請輸入個人專屬數字'),
  birth_year: Yup.string()
    .matches(/^\d{4}$/, '請輸入 4 位數年份')
    .required('請輸入出生年份'),
  birth_month: Yup.string()
    .test('valid-month', '請輸入有效的月份 (1-12)', (value) => {
      if (!value) return false;
      if (!/^\d+$/.test(value)) return false;
      const month = parseInt(value, 10);
      return month >= 1 && month <= 12;
    })
    .required('請輸入出生月份'),
  birth_day: Yup.string()
    .test('valid-day', '請輸入有效的日期 (1-31)', (value) => {
      if (!value) return false;
      if (!/^\d+$/.test(value)) return false;
      const day = parseInt(value, 10);
      return day >= 1 && day <= 31;
    })
    .required('請輸入出生日期'),
  birth_hour: Yup.string()
    .oneOf(
      ['23_early', '01', '03', '05', '07', '09', '11', '13', '15', '17', '19', '21', '23_late'],
      '請選擇有效的時辰'
    )
    .required('請選擇出生時辰'),
  gender: Yup.string()
    .oneOf(['Male', 'Female'], '請選擇性別')
    .required('請選擇性別'),
}).test('age-range', '', function(values: any) {
  const { birth_year, birth_month, birth_day } = values;
  if (!birth_year || !birth_month || !birth_day) return true;
  if (!/^\d{4}$/.test(birth_year) || !/^\d+$/.test(birth_month) || !/^\d+$/.test(birth_day)) return true;
  const year = parseInt(birth_year, 10);
  const month = parseInt(birth_month, 10);
  const day = parseInt(birth_day, 10);
  const birthDate = new Date(year, month - 1, day);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
  if (age < 15 || age > 80) {
    return this.createError({
      path: 'birth_year',
      message: `出生年份對應年齡約 ${age} 歲，本系統服務 15 至 80 歲之間的使用者`,
    });
  }
  return true;
});

/**
 * 註冊頁面組件
 */
export const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();

  // 表單狀態
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState<any>(null);

  // Phone verification state
  type RegistrationStep = 'form' | 'phone-verification' | 'completing';
  const [registrationStep, setRegistrationStep] = useState<RegistrationStep>('form');
  const [phoneVerificationError, setPhoneVerificationError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [internationalPhoneNumber, setInternationalPhoneNumber] = useState<string>('');
  const [phoneVerifyToken, setPhoneVerifyToken] = useState<string>('');

  /**
   * Step 1: Send SMS verification code via backend API
   */
  const sendSmsVerificationCode = async (values: any) => {
    setIsLoading(true);
    setError(null);
    setPhoneVerificationError(null);

    try {
      // Format phone number to international format
      const international = formatPhoneNumber(values.country_code, values.phone_number);
      setInternationalPhoneNumber(international);

      console.log('[RegisterPage] Sending SMS to:', international);

      const result = await sendSmsCode(international);

      if (result && result.success === true) {
        console.log('[RegisterPage] SMS sent successfully');
        setRegistrationStep('phone-verification');
      } else {
        const msg = result?.message || '發送驗證碼失敗';
        console.error('[RegisterPage] SMS send failed:', msg);
        setRegistrationStep('form');
        setError(msg);
      }
    } catch (err: any) {
      console.error('[RegisterPage] SMS send error:', err);
      setRegistrationStep('form');
      setError(err?.response?.data?.message || err.message || '發送驗證碼失敗，請稍後再試');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Step 2: Verify SMS code via backend API and complete registration
   */
  const handleVerifyCode = async (code: string) => {
    if (!pendingFormValues || !internationalPhoneNumber) {
      console.error('[RegisterPage] Missing form values or phone number');
      return;
    }

    setIsVerifying(true);
    setPhoneVerificationError(null);

    try {
      console.log('[RegisterPage] Verifying code...');

      const result = await verifySmsCode(internationalPhoneNumber, code);

      if (!result.success) {
        console.error('[RegisterPage] Code verification failed:', result.message);
        setPhoneVerificationError(result.message || '驗證碼錯誤');
        setIsVerifying(false);
        return;
      }

      console.log('[RegisterPage] Code verified successfully');

      // 儲存一次性 verify_token，後續 completeRegistration 送出給後端
      if (result.verify_token) {
        setPhoneVerifyToken(result.verify_token);
      }

      // Continue with registration
      setRegistrationStep('completing');
      await completeRegistration(pendingFormValues, result.verify_token ?? '');
    } catch (err: any) {
      console.error('[RegisterPage] Verification error:', err);
      setPhoneVerificationError(err.message || '驗證失敗');
      setIsVerifying(false);
    }
  };

  /**
   * 步驟 3：完成註冊（電話已驗證）
   */
  const completeRegistration = async (values: any, verifyToken: string = '') => {
    try {
      console.log('📝 完成註冊...');

      // 組合出生時間 (YYYYMMDDhh)
      // 子時凌晨 (00:01-01:00)：後端 LunarDayConvert 遇到 hh=23 會自動 +1 天，
      // 所以前端必須先將日期 -1 天，確保後端計算到正確的農曆日期。
      // 子時深夜 (23:01-24:00)：直接用當天日期即可，後端 +1 天後為隔天（正確）。
      const birth_hour_value = (values.birth_hour === '23_early' || values.birth_hour === '23_late')
        ? '23' : values.birth_hour;
      let birthYear = parseInt(values.birth_year, 10);
      let birthMonth = parseInt(values.birth_month, 10);
      let birthDay = parseInt(values.birth_day, 10);
      if (values.birth_hour === '23_early') {
        const d = new Date(birthYear, birthMonth - 1, birthDay);
        d.setDate(d.getDate() - 1);
        birthYear = d.getFullYear();
        birthMonth = d.getMonth() + 1;
        birthDay = d.getDate();
      }
      const formattedMonth = birthMonth.toString().padStart(2, '0');
      const formattedDay = birthDay.toString().padStart(2, '0');
      const birth_time = `${birthYear}${formattedMonth}${formattedDay}${birth_hour_value}`;

      // 組合完整電話號碼（儲存格式）
      let fullPhoneNumber = values.phone_number;
      if (values.country_code === '+886') {
        // 台灣：統一儲存為 09XXXXXXXX 格式
        if (values.phone_number.startsWith('0')) {
          fullPhoneNumber = values.phone_number;
        } else {
          fullPhoneNumber = '0' + values.phone_number;
        }
      } else {
        // 其他地區：儲存為國際格式
        fullPhoneNumber = values.country_code + values.phone_number;
      }

      // 呼叫註冊 API
      await register({
        email: values.email,
        password: values.password,
        full_name: values.full_name,
        phone_number: fullPhoneNumber,
        birth_time,
        gender: values.gender as 'Male' | 'Female',
        secret_code: values.secret_code,
        verify_token: verifyToken || phoneVerifyToken,
      });

      console.log('✅ 後端註冊成功');

      // 建立 Firebase Auth 帳號並寄送驗證信（非阻斷，失敗不影響登入）
      try {
        await createFirebaseUserAndSendVerification(values.email, values.password);
        console.log('✅ Firebase 驗證信已寄出');
      } catch (fbErr) {
        console.warn('[RegisterPage] Firebase email send failed (non-fatal):', fbErr);
      }

      // 註冊成功，重定向到主頁
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      console.error('❌ 註冊失敗:', err);

      // 判斷錯誤類型並提供對應訊息
      let errorMessage = '註冊失敗，請檢查輸入資料';

      if (!err.response && err.request) {
        errorMessage = '無法連線伺服器，請確認後端服務是否啟動';
      } else if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        errorMessage = '請求超時，請檢查網路連線或稍後再試';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      // 返回到表單頁面並顯示錯誤
      setError(errorMessage);
      setRegistrationStep('form');
      setPhoneVerificationError(null);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Resend SMS verification code
   */
  const handleResendCode = async () => {
    if (!internationalPhoneNumber) {
      console.error('[RegisterPage] Missing phone number for resend');
      return;
    }

    setPhoneVerificationError(null);

    try {
      console.log('[RegisterPage] Resending code...');
      const result = await sendSmsCode(internationalPhoneNumber);

      if (result.success) {
        console.log('[RegisterPage] Code resent successfully');
      } else {
        setPhoneVerificationError(result.message || '重新發送失敗');
      }
    } catch (err: any) {
      console.error('[RegisterPage] Resend error:', err);
      setPhoneVerificationError(err.message || '重新發送失敗');
    }
  };

  /**
   * Back to form
   */
  const handleBackToForm = () => {
    setRegistrationStep('form');
    setPhoneVerificationError(null);
    setInternationalPhoneNumber('');
  };

  /**
   * 處理取消（不同意條款）
   */
  const handleCancelTerms = () => {
    setShowTermsDialog(false);
    setPendingFormValues(null);
    setIsLoading(false);
  };

  /**
   * Formik 表單管理
   */
  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      confirmPassword: '',
      full_name: '',
      country_code: '+886',
      phone_number: '',
      secret_code: '',
      birth_year: '',
      birth_month: '',
      birth_day: '',
      birth_hour: '',
      gender: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      // 先顯示服務條款，等使用者同意後再註冊
      setPendingFormValues(values);
      setShowTermsDialog(true);
    },
  });

  /**
   * 處理同意條款並繼續註冊
   */
  const handleAgreeTermsAndRegister = async () => {
    setShowTermsDialog(false);
    if (pendingFormValues) {
      // 發送 SMS 驗證碼（步驟 1）
      await sendSmsVerificationCode(pendingFormValues);
      // 注意：pendingFormValues 不清除，稍後驗證碼確認後需要用到
    }
  };

  /**
   * 簡易 Markdown 渲染器（支援 # ## ### **bold** --- - 清單）
   * 不需要外部套件，只使用 MUI 元件。
   */
  const renderMarkdown = (content: string): React.ReactNode => {
    const parseBold = (text: string): React.ReactNode => {
      const parts = text.split(/\*\*(.*?)\*\*/g);
      if (parts.length === 1) return text;
      return parts.map((part, i) =>
        i % 2 === 1 ? <strong key={i}>{part}</strong> : part
      );
    };

    return content.split('\n').map((line, index) => {
      if (line.startsWith('# ')) {
        return (
          <Typography key={index} variant="h6" fontWeight="bold"
            sx={{ mt: 2, mb: 0.5, color: 'text.primary' }}>
            {parseBold(line.slice(2))}
          </Typography>
        );
      }
      if (line.startsWith('## ')) {
        return (
          <Typography key={index} variant="subtitle1" fontWeight="bold"
            sx={{ mt: 2, mb: 0.5, borderBottom: '1px solid', borderColor: 'divider', pb: 0.5, color: 'text.primary' }}>
            {parseBold(line.slice(3))}
          </Typography>
        );
      }
      if (line.startsWith('### ')) {
        return (
          <Typography key={index} variant="body2" fontWeight="bold"
            sx={{ mt: 1.5, mb: 0.3, color: 'text.secondary' }}>
            {parseBold(line.slice(4))}
          </Typography>
        );
      }
      if (line === '---') {
        return <Divider key={index} sx={{ my: 1.5 }} />;
      }
      if (line.startsWith('- ')) {
        const text = line.startsWith('- [ ] ') ? line.slice(6) : line.slice(2);
        return (
          <Box key={index} sx={{ display: 'flex', gap: 0.5, pl: 1.5, mb: 0.3 }}>
            <Typography variant="body2" component="span" sx={{ flexShrink: 0, color: 'text.secondary' }}>•</Typography>
            <Typography variant="body2" component="span">{parseBold(text)}</Typography>
          </Box>
        );
      }
      if (line.trim() === '') {
        return <Box key={index} sx={{ height: 6 }} />;
      }
      return (
        <Typography key={index} variant="body2" sx={{ mb: 0.3 }}>
          {parseBold(line)}
        </Typography>
      );
    });
  };

  return (
    <AuthPageLayout
      pageTitle="紫微斗數算命系統"
      pageSubtitle="註冊新帳號"
    >
      {/* Step 2: Phone verification */}
      {registrationStep === 'phone-verification' && (
        <PhoneVerification
          phoneNumber={internationalPhoneNumber}
          isVerifying={isVerifying}
          error={phoneVerificationError}
          onVerify={handleVerifyCode}
          onResend={handleResendCode}
          onBack={handleBackToForm}
        />
      )}

      {/* 步驟 1：註冊表單 */}
      {registrationStep === 'form' && (
      <Paper
        elevation={24}
        sx={{
          p: 4,
          borderRadius: 3,
          background: '#ffffff',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.12)',
        }}
      >
        {/* 贈送點數提示 — 管理員若需修改此宣傳文字，請在此處直接修改 */}
        <Box textAlign="center" mb={3} sx={{
          bgcolor: '#fff8e1',
          border: '1px solid #ffe082',
          borderRadius: 2,
          px: 2,
          py: 1,
        }}>
          <Typography variant="body1" sx={{
            color: '#b45309',
            fontWeight: 'bold',
          }}>
            🎁 註冊即贈送 499 點體驗點數（30天內有效）
          </Typography>
        </Box>

          {/* 錯誤訊息 */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* 註冊表單 */}
          <form onSubmit={formik.handleSubmit}>
            <Box display="flex" flexDirection="column" gap={2.5}>
              {/* 帳號資訊 */}
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  帳號資訊
                </Typography>
                <Grid container spacing={2}>
                  <Grid item={true} xs={12}>
                    <TextField
                      fullWidth
                      id="email"
                      name="email"
                      label="Email"
                      type="email"
                      variant="outlined"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.email && Boolean(formik.errors.email)}
                      helperText={formik.touched.email && formik.errors.email}
                      disabled={isLoading}
                      autoComplete="email"
                    />
                  </Grid>

                  <Grid item={true} xs={12} sm={6}>
                    <TextField
                      fullWidth
                      id="password"
                      name="password"
                      label="密碼"
                      type={showPassword ? 'text' : 'password'}
                      variant="outlined"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.password && Boolean(formik.errors.password)}
                      helperText={formik.touched.password && formik.errors.password}
                      disabled={isLoading}
                      autoComplete="new-password"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              disabled={isLoading}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item={true} xs={12} sm={6}>
                    <TextField
                      fullWidth
                      id="confirmPassword"
                      name="confirmPassword"
                      label="確認密碼"
                      type={showConfirmPassword ? 'text' : 'password'}
                      variant="outlined"
                      value={formik.values.confirmPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.confirmPassword &&
                        Boolean(formik.errors.confirmPassword)
                      }
                      helperText={
                        formik.touched.confirmPassword && formik.errors.confirmPassword
                      }
                      disabled={isLoading}
                      autoComplete="new-password"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                              disabled={isLoading}
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* 個人資訊 */}
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  個人資訊
                </Typography>
                <Grid container spacing={2}>
                  <Grid item={true} xs={12} sm={6}>
                    <TextField
                      fullWidth
                      id="full_name"
                      name="full_name"
                      label="姓名或代稱"
                      variant="outlined"
                      value={formik.values.full_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.full_name && Boolean(formik.errors.full_name)}
                      helperText={formik.touched.full_name && formik.errors.full_name}
                      disabled={isLoading}
                      autoComplete="name"
                    />
                  </Grid>

                  <Grid item={true} xs={12} sm={6}>
                    <Box display="flex" gap={1}>
                      <TextField
                        select
                        id="country_code"
                        name="country_code"
                        label="國碼"
                        variant="outlined"
                        value={formik.values.country_code}
                        onChange={(e) => {
                          formik.handleChange(e);
                          // 切換國碼時清空電話號碼
                          formik.setFieldValue('phone_number', '');
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.country_code && Boolean(formik.errors.country_code)}
                        disabled={isLoading}
                        sx={{ minWidth: 140 }}
                      >
                        {COUNTRY_CODE_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        fullWidth
                        id="phone_number"
                        name="phone_number"
                        label="手機號碼"
                        variant="outlined"
                        placeholder={
                          COUNTRY_CODE_OPTIONS.find(o => o.value === formik.values.country_code)?.placeholder || '912345678'
                        }
                        value={formik.values.phone_number}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.phone_number && Boolean(formik.errors.phone_number)
                        }
                        helperText={
                          formik.touched.phone_number && formik.errors.phone_number
                        }
                        disabled={isLoading}
                        autoComplete="tel"
                        inputProps={{
                          maxLength: COUNTRY_CODE_OPTIONS.find(o => o.value === formik.values.country_code)?.maxLength || 11,
                          inputMode: 'numeric',
                        }}
                      />
                    </Box>
                  </Grid>

                  <Grid item={true} xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      id="gender"
                      name="gender"
                      label="性別"
                      variant="outlined"
                      value={formik.values.gender}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.gender && Boolean(formik.errors.gender)}
                      helperText={formik.touched.gender && formik.errors.gender}
                      disabled={isLoading}
                    >
                      {GENDER_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item={true} xs={12}>
                    <TextField
                      fullWidth
                      id="secret_code"
                      name="secret_code"
                      label="個人專屬數字（4位數字）"
                      variant="outlined"
                      placeholder="如：身份證後四碼"
                      value={formik.values.secret_code}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.secret_code && Boolean(formik.errors.secret_code)}
                      helperText={
                        (formik.touched.secret_code && formik.errors.secret_code) ||
                        '請輸入專屬於您的 4 位數字（建議使用身份證後四碼，這是您出生時就擁有的獨特數字）'
                      }
                      disabled={isLoading}
                      inputProps={{ maxLength: 4, inputMode: 'numeric', pattern: '[0-9]*' }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* 出生時間 */}
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  出生時間 (用於排命盤)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item={true} xs={12} sm={4}>
                    <TextField
                      fullWidth
                      id="birth_year"
                      name="birth_year"
                      label="年"
                      variant="outlined"
                      placeholder="1990"
                      value={formik.values.birth_year}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.birth_year && Boolean(formik.errors.birth_year)}
                      helperText={formik.touched.birth_year && formik.errors.birth_year}
                      disabled={isLoading}
                    />
                  </Grid>

                  <Grid item={true} xs={6} sm={4}>
                    <TextField
                      fullWidth
                      id="birth_month"
                      name="birth_month"
                      label="月"
                      variant="outlined"
                      placeholder="3 或 03"
                      value={formik.values.birth_month}
                      onChange={(e) => {
                        formik.handleChange(e);
                        const val = e.target.value;
                        // 已輸入 2 位、或超出範圍時立即顯示錯誤
                        if (val.length === 2 || (val.length > 0 && parseInt(val) > 12)) {
                          formik.setFieldTouched('birth_month', true, true);
                        }
                      }}
                      onBlur={(e) => {
                        formik.handleBlur(e);
                        // 離開欄位時自動補零
                        const val = e.target.value;
                        if (val && val.length === 1 && /^[1-9]$/.test(val)) {
                          formik.setFieldValue('birth_month', val.padStart(2, '0'));
                        }
                      }}
                      error={formik.touched.birth_month && Boolean(formik.errors.birth_month)}
                      helperText={formik.touched.birth_month && formik.errors.birth_month}
                      disabled={isLoading}
                      inputProps={{ maxLength: 2, inputMode: 'numeric' }}
                    />
                  </Grid>

                  <Grid item={true} xs={6} sm={4}>
                    <TextField
                      fullWidth
                      id="birth_day"
                      name="birth_day"
                      label="日"
                      variant="outlined"
                      placeholder="8 或 08"
                      value={formik.values.birth_day}
                      onChange={(e) => {
                        formik.handleChange(e);
                        const val = e.target.value;
                        // 已輸入 2 位、或超出範圍時立即顯示錯誤
                        if (val.length === 2 || (val.length > 0 && parseInt(val) > 31)) {
                          formik.setFieldTouched('birth_day', true, true);
                        }
                      }}
                      onBlur={(e) => {
                        formik.handleBlur(e);
                        // 離開欄位時自動補零
                        const val = e.target.value;
                        if (val && val.length === 1 && /^[1-9]$/.test(val)) {
                          formik.setFieldValue('birth_day', val.padStart(2, '0'));
                        }
                      }}
                      error={formik.touched.birth_day && Boolean(formik.errors.birth_day)}
                      helperText={formik.touched.birth_day && formik.errors.birth_day}
                      disabled={isLoading}
                      inputProps={{ maxLength: 2, inputMode: 'numeric' }}
                    />
                  </Grid>

                  <Grid item={true} xs={12}>
                    <TextField
                      fullWidth
                      select
                      id="birth_hour"
                      name="birth_hour"
                      label="時辰"
                      variant="outlined"
                      value={formik.values.birth_hour}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.birth_hour && Boolean(formik.errors.birth_hour)}
                      helperText={formik.touched.birth_hour && formik.errors.birth_hour}
                      disabled={isLoading}
                    >
                      {HOUR_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </Box>

              {/* 註冊按鈕 */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                startIcon={<RegisterIcon />}
                sx={{ mt: 1 }}
              >
                {isLoading ? '註冊中...' : '註冊'}
              </Button>

              {/* 已有帳號提示 */}
              <Box textAlign="center" mt={2}>
                <Typography variant="body2">
                  已經有帳號了？{' '}
                  <Link
                    component={RouterLink}
                    to="/login"
                    sx={{ fontWeight: 'bold', textDecoration: 'none' }}
                  >
                    立即登入
                  </Link>
                </Typography>
              </Box>
            </Box>
          </form>
        </Paper>
      )}

      {/* 頁尾資訊 */}
      <Box textAlign="center" mt={3}>
        <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.5)' }}>
          © 2026 HowYun.Me 我的好運氣 - 紫微斗數算命系統. All rights reserved.
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.45)', mt: 0.5 }}>
          有註冊或使用上的問題，請聯絡{' '}
          <Link href="mailto:howyun.service@zetaaxis.com" sx={{ color: 'rgba(0, 0, 0, 0.55)' }}>
            howyun.service@zetaaxis.com
          </Link>
        </Typography>
      </Box>

      {/* 服務條款對話框 */}
      <Dialog
        open={showTermsDialog}
        onClose={handleCancelTerms}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
          使用者服務條款與同意書
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ lineHeight: 1.7 }}>
            {renderMarkdown(TERMS_OF_SERVICE)}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleCancelTerms}
            color="inherit"
            variant="outlined"
            disabled={isLoading}
          >
            不同意，取消註冊
          </Button>
          <Button
            onClick={handleAgreeTermsAndRegister}
            color="primary"
            variant="contained"
            disabled={isLoading}
            sx={{ minWidth: 180 }}
          >
            {isLoading ? '處理中...' : '同意，並繼續註冊'}
          </Button>
        </DialogActions>
      </Dialog>
    </AuthPageLayout>
  );
};

export default RegisterPage;
